/**
 * How fresh the register is, in one line.
 *
 * This replaces two stacked cards in the page head - one for the mirror, one for
 * the ranking pass - which between them carried a count, two dates, two progress
 * readouts and four buttons before the operator had read a single company. The
 * page head is not where that belongs: it is where you glance to check nothing
 * is broken, and everything you might PRESS now lives in `DataMenu`.
 *
 * What survives is the one fact a glance is for: is the mirror current, and is
 * anything running right now. A mark plus a time says both, and a run in flight
 * says so itself rather than hiding inside a menu nobody opened.
 *
 * A mark, not a bare colour: the four states have to stay apart on a bad monitor
 * and for a colour-blind reader, which is the same rule `StatusPill` follows.
 */
import type { ReactNode } from "react";
import { CheckCircle2, Database, Gauge, Loader2, XCircle } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import { datasetLabel } from "../../../../lib/datasetLabel";
import { fmtNum, fmtStamp } from "../../../ui";
import type { SyncState } from "./useSyncStatus";
import type { WebNeedPassState } from "./useWebNeedPass";

type Tone = "ok" | "bad" | "busy" | "mut";

function Line({ tone, children }: { tone: Tone; children: ReactNode }) {
  return <span className={cn("crm-fresh", `is-${tone}`)}>{children}</span>;
}

export function DataStatus({ sync, pass }: { sync: SyncState; pass: WebNeedPassState }) {
  const t = useT();
  const { running, lastRun } = sync;
  const progress = pass.progress;

  return (
    <span className="crm-freshbar">
      {running ? (
        <Line tone="busy">
          <Loader2 className="size-3.5 animate-spin" />
          {t("ariregister.sync.runningDs", {
            ds: datasetLabel(t, running.dataset),
            rows: fmtNum(running.rowsUpserted),
          })}
        </Line>
      ) : lastRun?.status === "failed" ? (
        <Line tone="bad">
          <XCircle className="size-3.5" />
          {t("ariregister.sync.failedShort")}
        </Line>
      ) : lastRun ? (
        <Line tone="ok">
          <CheckCircle2 className="size-3.5" />
          {t("ariregister.sync.at", { at: fmtStamp(lastRun.finishedAt || lastRun.startedAt) })}
        </Line>
      ) : (
        <Line tone="mut">
          <Database className="size-3.5" />
          {t("ariregister.sync.never")}
        </Line>
      )}

      {/* The ranking pass only speaks up while it is working. Its date lives in
          the menu: it is a number to check before trusting the ranking, not
          something the page head has to repeat all day. */}
      {progress?.running ? (
        <Line tone="busy">
          <Gauge className="size-3.5" />
          {t("ariregister.webNeed.panel.running", {
            done: fmtNum(progress.doneThisRun),
            total: fmtNum(progress.total),
          })}
        </Line>
      ) : null}
    </span>
  );
}
